import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { lessonsApi, type Lesson } from "../lib/lessons";
import { AuthButton } from "../components/AuthButton";

function fmtDate(ts: number): string {
  return new Date(ts).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function DashboardPage(): JSX.Element {
  const { ready, enabled, user } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<Lesson[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    lessonsApi
      .listMine()
      .then((r) => setLessons(r.lessons))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [user]);

  if (!ready) {
    return (
      <div className="h-full flex items-center justify-center bg-ink-900 text-ink-400">
        <div className="w-6 h-6 border-2 border-ink-700 border-t-lime-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-white p-6">
        <h1 className="text-2xl font-semibold mb-2">Tutor</h1>
        <p className="text-ink-400 text-sm max-w-md text-center mb-6">
          Platforma lekcji wideo dla nauczycieli i uczniów. Zaloguj się, żeby zobaczyć swoje lekcje.
        </p>
        {enabled ? <AuthButton variant="inline" /> : (
          <div className="text-ink-500 text-sm">Logowanie wyłączone (KEYCLOAK_* brak w env)</div>
        )}
        <Link to="/asystent" className="mt-8 text-xs text-ink-500 underline">
          Asystent AI (demo bez logowania) →
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-white">
      <header className="px-6 py-4 border-b border-ink-700 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Tutor</h1>
          <p className="text-[10px] text-ink-500 uppercase tracking-widest">
            Cześć, {user.profile.name || user.profile.preferred_username || user.profile.email}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/calendar" className="text-xs text-ink-300 hover:text-white border border-ink-600 rounded-lg px-3 py-1.5">
            Kalendarz
          </Link>
          <Link to="/book" className="text-xs text-ink-300 hover:text-white border border-ink-600 rounded-lg px-3 py-1.5">
            Znajdź nauczyciela
          </Link>
          <Link to="/asystent" className="text-xs text-ink-300 hover:text-white border border-ink-600 rounded-lg px-3 py-1.5">
            Asystent AI
          </Link>
          <AuthButton variant="header" />
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Moje lekcje</h2>
          <button
            onClick={() => navigate("/lessons/new")}
            className="bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold rounded-lg px-4 py-2 text-sm"
          >
            + Zaplanuj lekcję
          </button>
        </div>

        {loading && <div className="text-ink-400 text-sm">Wczytywanie…</div>}
        {error && (
          <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-2 text-red-200 text-sm">
            {error}
          </div>
        )}
        {lessons && lessons.length === 0 && (
          <div className="bg-ink-800 border border-ink-700 rounded-2xl p-6 text-center text-ink-400">
            Nie masz jeszcze żadnych lekcji.
          </div>
        )}
        <ul className="space-y-2">
          {lessons?.map((l) => (
            <li key={l.id}>
              <Link
                to={`/lessons/${l.id}`}
                className="block bg-ink-800 hover:bg-ink-700 border border-ink-700 rounded-2xl p-4"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-medium">{l.title}</span>
                  <StatusBadge status={l.status} />
                </div>
                <div className="text-xs text-ink-400 mt-1">
                  {fmtDate(l.starts_at)} – {fmtDate(l.ends_at)}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

function StatusBadge({ status }: { status: Lesson["status"] }): JSX.Element {
  const map: Record<Lesson["status"], string> = {
    scheduled: "text-ink-300 border-ink-600",
    live: "text-lime-400 border-lime-500",
    ended: "text-ink-500 border-ink-700",
    cancelled: "text-red-300 border-red-700",
  };
  const label: Record<Lesson["status"], string> = {
    scheduled: "zaplanowana",
    live: "● live",
    ended: "zakończona",
    cancelled: "anulowana",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider border rounded px-2 py-0.5 ${map[status]}`}>
      {label[status]}
    </span>
  );
}
